import { LoadingDots } from "PFComponents/loading_dots";
import EchartsWrapper from "PFCore/helpers/echarts_wrapper";
import React, { Dispatch, SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";

import DateGranularitySelector from "../../../common/date_granularity_selector";
import LinePreview from "../../../previews/line_preview";
import css from "./budget_vs_forecast.module.scss";
import { budgetVsForecastConfig } from "./config/budget_vs_forecast_config";

interface IDemand {
  week_start?: string;
  requested_hours: number;
  booked_hours: number;
}

type BudgetVsForecastProps = {
  data: IDemand[] | null;
  dateGranularity: string;
  setDateGranularity: Dispatch<SetStateAction<string>>;
  loading: boolean;
  error: boolean;
};

const BudgetVsForecast = ({
  data,
  dateGranularity,
  setDateGranularity,
  loading,
  error
}: BudgetVsForecastProps) => {
  const { t } = useTranslation("insights");

  const selectedAxis = {
    daily: "date",
    weekly: "week_start",
    monthly: "month_name",
    undefined: "date"
  }[dateGranularity];

  const [xAxis, series] = useMemo(() => {
    const axis = data ? data.map((item) => item[selectedAxis || ""]) : [];
    const budget = data ? data.map((item) => item.requested_hours) : [];
    const forecast = data ? data.map((item) => item.booked_hours) : [];

    return [axis, { budget, forecast }];
  }, [data?.length]);

  const options = useMemo(
    () => budgetVsForecastConfig(xAxis, series, t("bookingReports.budget"), t("bookingReports.forecast")),
    [xAxis, series]
  );

  return (
    <div className={css.wrapper}>
      <div className={css.header}>
        <h3>{t("bookingReports.budgetVsForecast")}</h3>
        <DateGranularitySelector selectedDate={dateGranularity} setDateGranularity={setDateGranularity} />
      </div>
      {loading && !error && <LoadingDots circlesEnabled pageViewEnabled />}
      {error && <LinePreview animated content={t("noData")} />}
      {data && !loading && !error && <EchartsWrapper options={options} height={600} />}
    </div>
  );
};

export default BudgetVsForecast;
